import { memo, useMemo } from 'react';
import { X, CalendarDays, MapPin, Globe, Phone, Mail, Zap, Copy, Navigation } from 'lucide-react';
import {
  ORANGELABEL_LOGO, ABIB_LOGO,
  getDisplayRegion, formatEventTime, getWebsiteHref, openDirections,
  STATE_BOUNDS
} from './MapHelpers';
import PlatformIcon from './PlatformIcon';
import SourceLogo from './SourceLogo';
import { parseLocalDate } from '../../utils/date';
import { safeHref } from '../../utils/url';
import { copyToClipboard } from '../../utils/clipboard';

// Detect which state the map is currently viewing
const detectCurrentState = (bounds) => {
  if (!bounds) return null;

  const stateNames = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

  for (const state of stateNames) {
    const stateBound = STATE_BOUNDS[state];
    if (stateBound) {
      // Check if map bounds are mostly within this state
      const intersection = bounds.intersects(stateBound);
      const stateCenter = stateBound.getCenter();
      if (intersection && bounds.contains(stateCenter)) {
        return state;
      }
    }
  }
  return null;
};

// Check if a business is in a specific state.
// `state` is a short code (NSW/VIC/QLD/…) derived from the map viewport.
// Matches against: ISO 3166-2 on service_state / based_state (canonical
// new fields), legacy free-text in based_state / region / addr_state.
const isBusinessInState = (business, state) => {
  if (!state) return false;

  const stateVariants = {
    'NSW': ['NSW', 'New South Wales', 'Sydney', 'Greater Sydney', 'AU-NSW'],
    'VIC': ['VIC', 'Victoria', 'Melbourne', 'AU-VIC'],
    'QLD': ['QLD', 'Queensland', 'Brisbane', 'South East Queensland', 'Sunshine Coast', 'AU-QLD'],
    'SA':  ['SA',  'South Australia', 'Adelaide', 'AU-SA'],
    'WA':  ['WA',  'Western Australia', 'Perth', 'AU-WA'],
    'TAS': ['TAS', 'Tasmania', 'Hobart', 'AU-TAS'],
    'NT':  ['NT',  'Northern Territory', 'Darwin', 'AU-NT'],
    'ACT': ['ACT', 'Australian Capital Territory', 'Canberra', 'AU-ACT'],
  };

  const variants = (stateVariants[state] || [state]).map(v => v.toLowerCase());
  const candidates = [
    business.service_state,
    business.based_state,
    business.region,
    business.addr_state,
  ].map(v => (v || '').toLowerCase()).filter(Boolean);

  return candidates.some(c => variants.some(v => c.includes(v) || c === v));
};

// Check if business is national (Australia-Wide, Global, etc.)
const isGlobalBusiness = (business) => (
  business.service_scope === 'global' ||
  business.service_area === 'global' ||
  (business.region || '').toLowerCase() === 'global'
);

const isNationalBusiness = (business) => {
  if (business.service_scope === 'country') return true;
  const region = (business.region || '').toLowerCase();
  return region.includes('australia') || region === 'national';
};

// BTCMap "boosted" merchants — `boosted_until` is an ISO timestamp set
// by BTCMap when someone pays sats to promote a listing. We surface
// these with a pulsing cyan glow (see `.boosted-glow` in index.css) and
// bump them to the top of the sidebar list so paid attention is visible.
// ISO-8601 strings compare lexicographically, so a plain string compare
// against `now.toISOString()` is correct without parsing.
const isBoosted = (business) => {
  const until = business && business.boosted_until;
  if (!until || typeof until !== 'string') return false;
  return until > new Date().toISOString();
};

const Sidebar = memo(({
  showSidebar,
  setShowSidebar,
  visibleBusinesses,
  events,
  mapBounds,
  showOrangeLabel,
  setShowOrangeLabel,
  showBtcMap,
  setShowBtcMap,
  showEvents,
  setShowEvents,
  showATMs,
  setShowATMs,
  selectedBusiness,
  onSelectBusiness,
  onFlyToEvent,
  hasActiveFilters,
  resetFilters
}) => {
  // Filter events to only show those in current map view - memoized
  const visibleEvents = useMemo(() => {
    if (!mapBounds) return events;
    return events.filter(event => {
      if (!event.latitude || !event.longitude) return false;
      return mapBounds.contains([event.latitude, event.longitude]);
    });
  }, [events, mapBounds]);

  // Detect current state being viewed and sort businesses accordingly
  const currentState = useMemo(() => detectCurrentState(mapBounds), [mapBounds]);

  const sortedBusinesses = useMemo(() => {
    // Sort order (all render in the first 50-item slice below):
    //   -1. BTCMap "boosted" (paid promotion, boosted_until in future) —
    //       overrides every other tier so the boost is actually visible
    //    0. State-matching businesses
    //    1. Global-scope businesses (Bitrefill et al.) — relevant everywhere,
    //       so they should never be hidden by the 50-item truncation
    //    2. National (country-scope) businesses
    //    3. Other (region/county-scope, BTCMap imports)
    // Within each tier, newest first by created_at.
    const businesses = [...visibleBusinesses];

    const tier = (b, inState) => {
      if (isBoosted(b)) return -1;
      if (inState) return 0;
      if (isGlobalBusiness(b)) return 1;
      if (isNationalBusiness(b)) return 2;
      return 3;
    };

    return businesses.sort((a, b) => {
      const aInState = currentState && isBusinessInState(a, currentState);
      const bInState = currentState && isBusinessInState(b, currentState);
      const tA = tier(a, aInState);
      const tB = tier(b, bInState);
      if (tA !== tB) return tA - tB;
      const aDate = a.created_at || a.updated_at || '2000-01-01';
      const bDate = b.created_at || b.updated_at || '2000-01-01';
      return bDate.localeCompare(aDate);
    });
  }, [visibleBusinesses, currentState]);

  if (!showSidebar) return null;

  return (
    <>
      {/* Overlay for mobile */}
      <div
        className="md:hidden fixed inset-0 bg-black/30 z-[999]"
        onClick={() => setShowSidebar(false)}
      />

      <div className="fixed md:absolute top-0 md:top-16 left-0 md:left-4 bottom-0 md:bottom-4 w-[85%] md:w-80 z-[1000] md:z-[500] bg-background md:rounded-lg shadow-lg border border-border overflow-hidden flex flex-col">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between p-3 border-b bg-background">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2">
              <img src={ORANGELABEL_LOGO} alt="Orange Label" className="h-6 w-auto" />
              <span className="font-semibold text-sm text-foreground">Orange Label</span>
            </a>
            <span className="text-muted-foreground/30">|</span>
            <a href="/directory" className="text-sm font-medium text-primary hover:underline">Directory</a>
          </div>
          <button onClick={() => setShowSidebar(false)} className="p-2 hover:bg-accent rounded-full">
            <X className="w-5 h-5 text-foreground" />
          </button>
        </div>

        {/* Layer Filter Chips — 2×2 grid */}
        <div className="p-3 border-b bg-muted/50 dark:bg-card">
          <div className="grid grid-cols-2 gap-1.5">
            <button
              onClick={() => setShowOrangeLabel(prev => !prev)}
              className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                showOrangeLabel
                  ? 'bg-orange-100 border-orange-400 text-orange-700 dark:bg-orange-900/40 dark:border-orange-500 dark:text-orange-300'
                  : 'bg-background border-border text-muted-foreground hover:bg-accent'
              }`}
            >
              <div className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0"></div>
              Trades &amp; Services
            </button>
            <button
              onClick={() => setShowBtcMap(prev => !prev)}
              className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                showBtcMap
                  ? 'bg-cyan-100 border-cyan-500 text-cyan-700 dark:bg-cyan-900/40 dark:border-cyan-500 dark:text-cyan-300'
                  : 'bg-background border-border text-muted-foreground hover:bg-accent'
              }`}
            >
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{backgroundColor:'#0891B2'}}></div>
              Retail
            </button>
            <button
              onClick={() => setShowEvents(prev => !prev)}
              className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                showEvents
                  ? 'bg-blue-100 border-blue-400 text-blue-700 dark:bg-blue-900/40 dark:border-blue-500 dark:text-blue-300'
                  : 'bg-background border-border text-muted-foreground hover:bg-accent'
              }`}
            >
              <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></div>
              Events
            </button>
            <button
              onClick={() => setShowATMs(prev => !prev)}
              className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                showATMs
                  ? 'bg-violet-100 border-violet-400 text-violet-700 dark:bg-violet-900/40 dark:border-violet-500 dark:text-violet-300'
                  : 'bg-background border-border text-muted-foreground hover:bg-accent'
              }`}
            >
              <div className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0"></div>
              ATMs
            </button>
          </div>
        </div>

        <div className="px-3 py-2 border-b text-sm bg-background">
          <span className="text-muted-foreground">{sortedBusinesses.length} businesses</span>
          {showEvents && visibleEvents.length > 0 && (
            <span className="text-muted-foreground"> · <span className="text-blue-600 dark:text-blue-400">{visibleEvents.length} events</span></span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Events Section */}
          {showEvents && visibleEvents.length > 0 && (
            <div className="border-b border-border">
              <div className="px-4 py-3 bg-blue-50 dark:bg-blue-950/40 text-sm font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                <CalendarDays className="w-4 h-4" />
                Events in View ({visibleEvents.length})
              </div>
              <div className="divide-y divide-blue-100 dark:divide-blue-900/30">
                {visibleEvents.slice(0, 10).map(event => {
                  const eventUrl = safeHref(event.event_url);
                  const parsedDate = parseLocalDate(event.event_date);
                  const eventDateLabel = parsedDate && !Number.isNaN(parsedDate.getTime())
                    ? parsedDate.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
                    : null;
                  return (
                  <div
                    key={event.id}
                    className="p-3 bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100/80 dark:hover:bg-blue-950/50 cursor-pointer transition-colors"
                    onClick={() => onFlyToEvent(event)}
                  >
                    <div className="flex gap-2.5">
                      <PlatformIcon item={event} className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        {/* Title is the external link when available; whole
                            card still flies-to on click via the outer div. */}
                        {eventUrl ? (
                          <a
                            href={eventUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="font-semibold text-sm text-foreground hover:text-primary line-clamp-1"
                          >
                            {event.title}
                          </a>
                        ) : (
                          <h4 className="font-semibold text-sm text-foreground line-clamp-1">{event.title}</h4>
                        )}
                        {eventDateLabel && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5 font-medium line-clamp-1">
                            {eventDateLabel}
                            {event.event_time && ` · ${formatEventTime(event.event_time, event.state)}`}
                            {event.meetup_group_name && ` · ${event.meetup_group_name}`}
                          </p>
                        )}
                        {event.venue_name && (
                          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 line-clamp-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            {event.venue_name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
                {visibleEvents.length > 10 && (
                  <div className="p-3 text-center text-sm text-muted-foreground">
                    +{visibleEvents.length - 10} more events in view
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Business List */}
          {sortedBusinesses.length === 0 && (!showEvents || visibleEvents.length === 0) ? (
            <div className="p-6 text-center text-muted-foreground">
              <p className="text-base">No businesses found</p>
              {hasActiveFilters && <button onClick={resetFilters} className="mt-3 text-primary hover:underline text-sm">Clear filters</button>}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {/* The 50-item cap was hiding global-scope records behind
                  state/country-scope lists when the filtered set was large
                  (e.g. /map/au, /map/region/oceania). The list has its own
                  scroll container, so we render all matching entries. */}
              {sortedBusinesses.map(b => (
                <div
                  key={b.id}
                  className={`p-3 hover:bg-accent cursor-pointer transition-colors relative ${
                    selectedBusiness?.id === b.id ? 'bg-accent border-l-4 border-l-primary' : ''
                  } ${b.abib_member && !b.btc_map ? 'bg-orange-50/50 dark:bg-orange-950/20' : ''} ${
                    isBoosted(b) ? 'boosted-glow' : ''
                  }`}
                  onClick={() => onSelectBusiness(b)}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 flex flex-col items-center gap-1">
                      <SourceLogo business={b} size="lg" />
                      {b.payment_lightning && (
                        <Zap
                          className="w-4 h-4 text-yellow-500 fill-yellow-500 cursor-help"
                          title="Lightning Accepted"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Header row: name + contact icons on the right. Icons
                          replace the old "Web/Call/Email" pill row so the
                          card is one line shorter. `pr-6` was previously
                          reserving space for the ABIB badge; keep a small
                          right pad since the icons live in the same slot. */}
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-semibold text-base leading-tight text-foreground min-w-0">
                          {b.business_name}
                        </h4>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {b.abib_member && !b.btc_map && (
                            <img
                              src={ABIB_LOGO}
                              alt="ABIB Member"
                              title="Australian Bitcoin Industry Body Member"
                              className="h-6 flex-shrink-0"
                            />
                          )}
                          {safeHref(getWebsiteHref(b.website)) && (
                            <a
                              href={safeHref(getWebsiteHref(b.website))}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center w-7 h-7 bg-primary/10 hover:bg-primary/20 rounded text-primary"
                              onClick={e => e.stopPropagation()}
                              title="Open website in new tab"
                              aria-label="Open website"
                            >
                              <Globe className="w-4 h-4" />
                            </a>
                          )}
                          {b.contact && !b.contact.includes('@') && (
                            <a
                              href={`tel:${b.contact.replace(/\s/g, '')}`}
                              className="inline-flex items-center justify-center w-7 h-7 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 rounded text-green-700 dark:text-green-400"
                              onClick={e => e.stopPropagation()}
                              title="Call"
                              aria-label="Call"
                            >
                              <Phone className="w-4 h-4" />
                            </a>
                          )}
                          {b.contact && b.contact.includes('@') && (
                            <a
                              href={`mailto:${b.contact}`}
                              className="inline-flex items-center justify-center w-7 h-7 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded text-blue-700 dark:text-blue-400"
                              onClick={e => e.stopPropagation()}
                              title="Email"
                              aria-label="Email"
                            >
                              <Mail className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex items-start gap-1.5 text-sm text-muted-foreground mt-1">
                        <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span className="flex-1 line-clamp-1">{getDisplayRegion(b)}</span>
                      </div>
                      {/* Copy Address + Go — only render for records with real
                          coordinates. Online/service OL records (Bitrefill,
                          Proton VPN, Strike, etc.) have no physical address
                          and no lat/lng; "Copy Address" would copy a scope
                          label like "Global" and "Go" has no destination —
                          both add visual noise without any function. */}
                      {b.latitude && b.longitude && (
                        <div className="flex items-center justify-end gap-1.5 mt-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); copyToClipboard(getDisplayRegion(b), 'Address copied!'); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-muted hover:bg-accent rounded-full text-xs font-medium text-foreground"
                          >
                            <Copy className="w-3.5 h-3.5" /> Copy Address
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openDirections(b); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-500 hover:bg-blue-600 rounded-full text-xs font-medium text-white"
                          >
                            <Navigation className="w-3.5 h-3.5" /> Go
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
});

export default Sidebar;
